/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useId } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { colors, spacing, stroke } from "../../../../../global/emotion";
import { stylesScreenReader } from "../../../../../global/emotion/styles/stylesScreenReader";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";

const hallowElement: HallowElement = "input";

const labelStyle: Interpolation<ThemeType> = (theme) => ({
  borderColor: colors.transparent, // prevents element shifting on :checked
  borderStyle: "solid",
  borderWidth: stroke.regular,
  cursor: "pointer",
  height: "fit-content",
  lineHeight: 0,
  margin: "0 auto",
  outline: "none",
  padding: spacing.t,
  width: "fit-content",
  ":hover": {
    borderColor: theme.semanticColors.neutralsVeryLow,
  },
});
const radioImageLabelFocusStyle: Interpolation<ThemeType> = (theme) => ({
  borderColor: theme.semanticColors.accentMedium,
  ":hover": {
    borderColor: theme.semanticColors.accentMedium,
  },
});

export type RadioImageInputProps = Omit<
  HallowElementProps<typeof hallowElement>,
  "onClick" | "value"
> & {
  selected?: boolean;
  label: string;
  onClick?: Function;
  register: any;
  value: string;
};

export const RadioImageInput = ({
  children,
  selected,
  label,
  onClick = null,
  register,
  value,
  className,
  ...props
}: RadioImageInputProps) => {
  const id = useId();
  return (
    <>
      <input
        id={id}
        type="radio"
        value={value}
        onClick={(e) => {
          if (onClick)
            onClick(
              typeof value === "string"
                ? e.currentTarget.value
                : e.currentTarget.valueAsNumber,
            );
        }}
        {...register(label)}
        {...props}
        css={stylesScreenReader}
      />
      <label
        htmlFor={id}
        css={[labelStyle, selected && radioImageLabelFocusStyle]}
        className={className}
      >
        {children}
      </label>
    </>
  );
};
